// src/encounter/intelligence/catalog-adapters.ts
//
// `?x=intel-rail-catalog`: the thin, pure adapters that hand the engine's payloads to Antonin's approved Intelligence rail
// components (@scripsteam/signal, the Signal catalog's IntelligenceRail, Card, LabRow; scrips-signal-ds feat/signal
// ff5af208, 850c5184, fc326b6d). They map; they write no clinical sentence. Every clinical string is an engine field
// verbatim, or an engine-ruled fixed line already in explanation.ts / next-step.ts. Kept apart from the components so a
// component file exports components only (react-refresh). Nothing here logs.
import type { ComponentProps } from 'react';
import { CHIPS, type ChipSet, type IntelligenceRailCard, type LabRow } from '@scripsteam/signal';
import type { OrbState as DsOrbState } from '@scripsteam/scrips-signal-ds';
import { routeFor } from './chip-routes';
import { SESSION_EXPIRED, STATE_LINE, d45, inputsOf, labOf, VERDICT_WORD, type ExplanationV1, type ResultRowView, type Source } from './explanation';
import type { NextStepV1 } from './next-step';
import type { IntelligencePhase, RailScreenLabel } from './rail-words';
import type { TraceStep } from './run-card-state';
import { CHECK_NAME } from './ask-words';

type CardProps = ComponentProps<typeof IntelligenceRailCard>;
/** The catalog card's origin words come from its own map (record / rule / ai), keyed here by the engine's origin kind. */
export type CatalogOrigin = CardProps['origin'];
export type CatalogCardState = NonNullable<CardProps['state']>;
export type CatalogLabResult = ComponentProps<typeof LabRow>['r'];
export type CatalogProduced = NonNullable<CardProps['produced']>;

type Route = ReturnType<typeof routeFor>;
/** The routes the rail has a hook for today: Explain (use-explain), the code-built brief (use-brief) and an engine agent
 *  run (useIntelligenceRun, the hook Ask uses). */
const hooked = (r: Route) => r.kind === 'explain' || r.kind === 'run' || (r.kind === 'code' && r.source === 'brief');

/**
 * Rail lock §3 and §4 (4 Nov, rulings): only the chips and lenses whose route in chip-routes.ts has a hook here are offered —
 * his CHIPS for the screen, in his order, minus not-prepared labels and routes nothing feeds yet (Retests, Patient facts,
 * Retests on the plan, Orders): a control that does nothing is not drawn. A lone lens is drawn as a chip, since his lens pill
 * only opens the menu of the lenses after it.
 */
export function routedChips(screen: 'pre' | 'visit' | 'wrapup', only: (r: Route) => boolean = () => true): ChipSet {
  const offered = (label: string) => {
    const r = routeFor(screen, label);
    return hooked(r) && only(r);
  };
  const set = CHIPS[screen];
  const chips = set.chips.filter(offered);
  const lenses = set.lenses.filter(offered);
  return lenses.length === 1 ? { chips: [...chips, ...lenses], lenses: [] } : { chips, lenses };
}

/**
 * The ONE live Orb (orb-state.ts orbStateForAmbient: what the ambient scribe is doing) as his IntelOrb's states: recording is
 * his listening ripple, processing his thinking breath. His orb has no failed state (gap: the ambient panel says so itself),
 * so error and everything else is still.
 */
export function catalogOrbState(s: DsOrbState | undefined): 'idle' | 'thinking' | 'listening' {
  if (s === 'streaming') return 'listening';
  if (s === 'thinking') return 'thinking';
  return 'idle';
}

/** A brief line's record ids (its `cites`) as what his source marker opens: one row per cited record item. */
export function citesDetail(cites: string[]): [string, string][] {
  return cites.map((id, i) => [cites.length === 1 ? 'Record item' : `Record item ${i + 1}`, id]);
}

/** The rail's screen in his catalog for the encounter phase (its chip set and routing table). */
export function catalogScreen(phase: IntelligencePhase): 'pre' | 'visit' | 'wrapup' {
  return phase === 'Pre-visit' ? 'pre' : phase === 'Wrap-up' ? 'wrapup' : 'visit';
}

/** What the rail shows on a screen (rail lock §3, SAMER RULINGS 4 Nov). */
export interface RailScreenPlan {
  /** His screen id (the rail's data-frame). Documents has none of its own in his catalog: it uses Chart's. */
  screen: 'pre' | 'visit' | 'wrapup' | 'terrain' | 'chart' | 'timeline';
  chips: ChipSet;
  nextStep: boolean;
  brief: boolean;
  ask: boolean;
  explain: boolean;
}

/**
 * Per screen (Samer, 4 Nov; Antonin's catalogue): Pre-visit = Next best action + the prepared brief (on arrival; "Prepare"
 * asks again); Visit = Next best action + Ask; Wrap-up = Next best action; Terrain = "Explain a body system" + its card.
 * Body systems are Terrain's ONLY: no other screen offers the chip or draws the Explain card. Chart, Timeline and Documents
 * have no ruled card yet (gap, PR): they keep Next best action, the one patient-level card, and offer no chip.
 */
const isRun = (r: Route) => r.kind === 'run';

export function railScreenPlan(label: RailScreenLabel): RailScreenPlan {
  const none: ChipSet = { chips: [], lenses: [] };
  switch (label) {
    case 'Pre-visit':
      return { screen: 'pre', chips: routedChips('pre', (r) => r.kind !== 'explain'), nextStep: true, brief: true, ask: false, explain: false };
    // Visit and Wrap-up also offer any chip chip-routes.ts sends to an engine agent (a `run` route, e.g. "Draft the plan" to
    // plan-drafter once the routing table says so); never Explain or the brief there.
    case 'Visit':
      return { screen: 'visit', chips: routedChips('visit', isRun), nextStep: true, brief: false, ask: true, explain: false };
    case 'Wrap-up':
      return { screen: 'wrapup', chips: routedChips('wrapup', isRun), nextStep: true, brief: false, ask: false, explain: false };
    case 'Terrain':
      return { screen: 'terrain', chips: { chips: ['Explain a body system'], lenses: [] }, nextStep: false, brief: false, ask: false, explain: true };
    case 'Timeline':
      return { screen: 'timeline', chips: none, nextStep: true, brief: false, ask: false, explain: false };
    default:
      return { screen: 'chart', chips: none, nextStep: true, brief: false, ask: false, explain: false };
  }
}

/** O1: the engine's origin kind, as the catalog card's origin. No origin (an older payload) reads as an AI draft. */
export function catalogOrigin(kind: string | undefined): CatalogOrigin {
  if (kind === 'from-record') return 'record';
  if (kind === 'scrips-rule') return 'rule';
  return 'ai';
}

/**
 * A hook's fixed notice line as the catalog card's one-line state. The catalog writes the same words (rail-data.ts T:
 * busy, timeout, down); a line it has no state for (the expired session) is null, and the host draws it verbatim.
 */
export function noticeState(notice: string): CatalogCardState | null {
  if (notice === STATE_LINE.busy) return 'busy';
  if (notice === STATE_LINE.timeout) return 'timeout';
  if (notice === STATE_LINE.technical) return 'down';
  return null;
}

/** True for the line the host must draw itself (no catalog state carries it). */
export const isSessionLine = (notice: string) => notice === SESSION_EXPIRED;

/**
 * A refused or failed explanation as the catalog card's state: denied, consent (the card is cleared), or the one-line
 * failure. Withheld and a failed card that keeps critical rows are not mapped: the engine's own withheld line is drawn
 * verbatim (#825 rule 2a), which the catalog's fixed withheld copy cannot carry.
 */
export function explainState(x: ExplanationV1): CatalogCardState | null {
  if (x.state === 'answered' || x.state === 'withheld') return null;
  if (x.reason === 'access-denied') return 'denied';
  if (x.reason === 'consent-withdrawn') return 'consent';
  if (x.critical || x.withheldLine) return null;
  if (x.reason === 'busy') return 'busy';
  if (x.reason === 'timeout') return 'timeout';
  return 'down';
}

/** O2: "How this was produced" from the engine: each check's own state, the fixed line, the inputs, versions, output id. */
export function explainProduced(x: ExplanationV1): CatalogProduced {
  const inputs = inputsOf(x).map((s) => [s.name, s.value !== undefined ? `${s.value}${s.unit ? ` ${s.unit}` : ''}` : null, s.measuredOn ? d45(s.measuredOn) : null].filter(Boolean).join(' · '));
  const trace: [string, string][] = [];
  if (inputs.length) trace.push(['Inputs', inputs.join('; ')]);
  trace.push(['Versions', `${x.schema} · ${x.checkSet}`]);
  if (x.produced) trace.push(['Rules', `${x.produced.rules.name} · ${x.produced.rules.version}`]);
  if (x.produced?.model) trace.push([x.produced.model.role, x.produced.model.deployment]);
  trace.push(['Output', x.requestId]);
  return { checks: x.checks.map((c) => ({ name: c.name, state: c.state ?? VERDICT_WORD[c.verdict] })), ...(x.checksNote ? { note: x.checksNote } : {}), trace };
}

/** The Next step card is code-built by a Scrips rule: no checks ran, so none is listed; the trace is its origin, time, audit id. */
export function nextStepProduced(card: NextStepV1): CatalogProduced {
  return { checks: [], trace: [['Origin', card.origin.label], ['As of', card.asOf], ['Audit', card.auditId]] };
}

/** An Ask run's trace (run-card-state.ts traceOf) for "How this was produced": each check with its own verdict, then what the agent read. */
export function askProduced(trace: TraceStep[]): CatalogProduced {
  const word = { pass: 'passed', fail: 'did not pass', uncertain: 'uncertain' } as const;
  const checks = trace.flatMap((t) => (t.kind === 'check' ? [{ name: CHECK_NAME[t.check], state: word[t.verdict] }] : []));
  const rows: [string, string][] = trace.flatMap((t, i): [string, string][] =>
    t.kind === 'agent' ? [['Agent', t.model ? `${t.agent} (${t.model})` : t.agent]] : t.kind === 'tool' ? [[`Read ${i}`, t.count !== null ? `${t.name} (${t.count} items)` : t.name]] : [],
  );
  return { checks, trace: rows };
}

/** One engine result row (explanation.ts ResultRowView) as the catalog's LabRow. Every cell is the cited source's. */
export function labResultOf(row: ResultRowView): CatalogLabResult {
  const s = row.source;
  const lab = labOf(s.lab);
  const value = [`${s.comparator ?? ''}${s.value ?? ''}`, s.unit].filter(Boolean).join(' ');
  const r: CatalogLabResult = { id: s.recordId, test: s.name, value, date: row.date, lab, source: [lab, row.date].filter(Boolean).join(', ') };
  if (row.flag.kind !== 'blank') {
    r.flag = row.flag.text;
    r.flagNote = row.flag.aria;
  }
  if (row.flag.kind === 'critical') r.critical = true;
  if (row.range.kind === 'plain') r.range = row.range.text;
  // T3: a range Scrips did not compare opens as "See report" with the printed text.
  if (row.range.kind === 'report' && row.range.text) r.printed = row.range.text;
  // T11: every non-final status stays on the row (Prelim, Corrected, Amended, Status unknown).
  if (row.statusTag) r.status = row.statusTag;
  if (row.outside) r.r2bis = true;
  // A5: the row's source marker opens its record item: the engine sentence verbatim, the printed flag, the record id.
  r.detail = recordItemOf(row.sentence, s);
  return r;
}

/** A5: what a row's source marker opens (explain-card.tsx RecordItem): the engine sentence, the printed flag, the record id. */
export function recordItemOf(sentence: string, s: Pick<Source, 'printedFlag' | 'recordId'>): [string, string][] {
  return [['Source', sentence], ...(s.printedFlag ? ([['Laboratory flag', s.printedFlag]] as [string, string][]) : []), ['Record item', s.recordId]];
}

/** The engine's full limits lists (explain-card.tsx LimitsRow), headed as the OFF card heads them; empty lists are left out. */
export function limitsGroupsOf(full: ExplanationV1['limitsFull']): Array<{ head: string; items: string[] }> {
  if (!full) return [];
  return (
    [
      ['Read', full.read],
      ['Shown, not used in the summary', full.shownNotUsed],
      ['Not read', full.notRead],
    ] as const
  )
    .filter(([, items]) => items.length)
    .map(([head, items]) => ({ head, items: [...items] }));
}
